/**
 * The app's button. `type="button"` by default, so a button inside a form
 * never submits it by accident; `loading` swaps the icon for a spinner and
 * disables the button.
 */

import { LoaderCircle, type LucideIcon } from 'lucide-react'
import type { ButtonHTMLAttributes } from 'react'

import { buttonClass, cn, type ButtonSize, type ButtonVariant } from './styles'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    icon?: LucideIcon
    loading?: boolean
}

export default function Button({
    variant = 'primary',
    size = 'md',
    icon: Icon,
    loading = false,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
}: ButtonProps) {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={cn(buttonClass(variant, size), className)}
            {...rest}
        >
            {loading ? (
                <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
                Icon && <Icon className="h-4 w-4" aria-hidden="true" />
            )}
            {children}
        </button>
    )
}
